import React, { useState, useMemo, useCallback } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import StarIcon from "@mui/icons-material/Star";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import InfoIcon from "@mui/icons-material/Info";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Tooltip from "@mui/material/Tooltip";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  ReferenceLine,
  Cell,
} from "recharts";
import { formatName } from "../../../../helpers/helpers";
import {
  formatDateReadableWithDayOfWeek,
  formatDateReadableMonthAndDay,
} from "../../requests/utilities/helpers";

export default function TeamPerformanceTabDisplay(props) {
  const { surveys, teamStats, overallStats, dateRange } = props;

  // Sorting state
  const [orderBy, setOrderBy] = useState('avgRating');
  const [order, setOrder] = useState('desc');

  // Filtering state
  const [selectedRole, setSelectedRole] = useState('all');
  const [performanceFilter, setPerformanceFilter] = useState('all');
  const [minSampleSize, setMinSampleSize] = useState(5);

  // Modal state
  const [openModal, setOpenModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);

  // Helper Functions

  /**
   * Filter surveys by team member ID
   */
  const getStaffSurveys = useCallback((staffId) => {
    return surveys.filter(survey =>
      survey.staff_members.some(staff => staff.id === staffId)
    ).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  }, [surveys]);

  /**
   * Calculate role-based aggregates
   */
  const calculateRoleAverages = useMemo(() => {
    const roleGroups = teamStats.reduce((acc, member) => {
      if (!acc[member.role]) {
        acc[member.role] = { members: [], totalSurveys: 0, ratingSum: 0 };
      }
      acc[member.role].members.push(member);
      acc[member.role].totalSurveys += member.totalSurveys;
      acc[member.role].ratingSum += parseFloat(member.avgRating) * member.totalSurveys;
      return acc;
    }, {});

    return Object.entries(roleGroups).map(([role, data]) => ({
      role,
      avgRating: parseFloat((data.ratingSum / data.totalSurveys).toFixed(2)),
      totalSurveys: data.totalSurveys,
      memberCount: data.members.length,
    })).sort((a, b) => parseFloat(b.avgRating) - parseFloat(a.avgRating));
  }, [teamStats]);

  /**
   * Categorize performers by rating thresholds
   */
  const categorizePerformers = useMemo(() => {
    const reliableStats = teamStats.filter(m => m.totalSurveys >= minSampleSize);

    return {
      high: reliableStats.filter(m => parseFloat(m.avgRating) >= 4.0),
      average: reliableStats.filter(m => parseFloat(m.avgRating) >= 3.3 && parseFloat(m.avgRating) < 4.0),
      needsImprovement: reliableStats.filter(m => parseFloat(m.avgRating) < 3.3),
      insufficientData: teamStats.filter(m => m.totalSurveys < minSampleSize)
    };
  }, [teamStats, minSampleSize]);

  /**
   * Calculate trend direction for a staff member
   */
  const calculateTrendDirection = useCallback((staffId) => {
    const memberSurveys = getStaffSurveys(staffId);
    const now = new Date();
    const midpoint = new Date(now);
    midpoint.setDate(now.getDate() - Math.floor(dateRange / 2));

    // Format as YYYY-MM-DD to avoid UTC day shifts
    const formatDateString = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    const midpointStr = formatDateString(midpoint);

    const recentSurveys = memberSurveys.filter(s => s.date >= midpointStr);
    const olderSurveys = memberSurveys.filter(s => s.date < midpointStr);

    if (recentSurveys.length < 2 || olderSurveys.length < 2) {
      return { trend: 'insufficient', change: 0, recentAvg: 0, olderAvg: 0 };
    }

    const recentAvg = recentSurveys.reduce((sum, s) => sum + s.rating, 0) / recentSurveys.length;
    const olderAvg = olderSurveys.reduce((sum, s) => sum + s.rating, 0) / olderSurveys.length;
    const change = recentAvg - olderAvg;

    return {
      trend: change > 0.1 ? 'improving' : change < -0.1 ? 'declining' : 'stable',
      change: parseFloat(change.toFixed(2)),
      recentAvg: parseFloat(recentAvg.toFixed(2)),
      olderAvg: parseFloat(olderAvg.toFixed(2)),
      recentCount: recentSurveys.length,
      olderCount: olderSurveys.length
    };
  }, [getStaffSurveys, dateRange]);

  /**
   * Calculate daily trend data for a staff member
   */
  const calculateMemberTrend = useCallback((staffId) => {
    const memberSurveys = getStaffSurveys(staffId);
    const dailyData = {};

    memberSurveys.forEach(survey => {
      const date = survey.date;
      if (!dailyData[date]) {
        dailyData[date] = { date, ratings: [], count: 0 };
      }
      dailyData[date].ratings.push(survey.rating);
      dailyData[date].count++;
    });

    return Object.values(dailyData)
      .map(day => ({
        date: day.date,
        avgRating: parseFloat((day.ratings.reduce((a, b) => a + b, 0) / day.count).toFixed(2)),
        count: day.count
      }))
      .sort((a, b) => a.date.localeCompare(b.date));  // String comparison for YYYY-MM-DD
  }, [getStaffSurveys]);

  // Get unique roles for filter
  const uniqueRoles = useMemo(() => {
    const roles = new Set(teamStats.map(m => m.role));
    return Array.from(roles).sort();
  }, [teamStats]);

  // Apply filters and sorting
  const sortedAndFilteredStats = useMemo(() => {
    let filtered = teamStats;

    // Minimum sample size filter
    filtered = filtered.filter(m => m.totalSurveys >= minSampleSize);

    // Role filter
    if (selectedRole !== 'all') {
      filtered = filtered.filter(m => m.role === selectedRole);
    }

    // Performance filter
    if (performanceFilter === 'high') {
      filtered = filtered.filter(m => parseFloat(m.avgRating) >= 4.0);
    } else if (performanceFilter === 'needsImprovement') {
      filtered = filtered.filter(m => parseFloat(m.avgRating) < 3.3);
    } else if (performanceFilter === 'improving') {
      filtered = filtered.filter(m => {
        const trend = calculateTrendDirection(m.id);
        return trend.trend === 'improving';
      });
    } else if (performanceFilter === 'declining') {
      filtered = filtered.filter(m => {
        const trend = calculateTrendDirection(m.id);
        return trend.trend === 'declining';
      });
    }

    // Sort
    const sorted = [...filtered].sort((a, b) => {
      const aVal = a[orderBy];
      const bVal = b[orderBy];
      if (order === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });

    return sorted;
  }, [teamStats, selectedRole, performanceFilter, orderBy, order, calculateTrendDirection, minSampleSize]);

  // Event handlers
  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleRowClick = useCallback((member) => {
    setSelectedMember(member);
    setOpenModal(true);
  }, []);

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedMember(null);
  };

  // Custom tooltip for role chart
  const CustomRoleTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;

    const data = payload[0].payload;
    return (
      <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
        <p className="font-bold text-gray-900">{data.role}</p>
        <p className="text-sm text-gray-600 mt-1">
          Average Rating: <span className="font-semibold">{data.avgRating}</span>
        </p>
        <p className="text-sm text-gray-600">
          Team Members: <span className="font-semibold">{data.memberCount}</span>
        </p>
        <p className="text-sm text-gray-600">
          Total Surveys: <span className="font-semibold">{data.totalSurveys}</span>
        </p>
      </div>
    );
  };

  // Handle no data
  if (teamStats.length === 0) {
    return (
      <div className="bg-white p-12 rounded-lg shadow border border-gray-200 text-center">
        <p className="text-gray-500 text-lg">No team performance data available</p>
        <p className="text-gray-400 text-sm mt-2">No surveys with staff member data found for the selected date range</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Performance Filters & Controls */}
      <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            {/* Role Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Role</label>
              <Select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                sx={{
                  backgroundColor: "white",
                  borderRadius: "10px",
                  height: "42px",
                  minWidth: "300px",
                  width: "300px",
                  ".MuiOutlinedInput-notchedOutline": {
                    borderWidth: "1px",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#6d28d9",
                  },
                }}
              >
                <MenuItem value="all">All Roles</MenuItem>
                {uniqueRoles.map(role => (
                  <MenuItem key={role} value={role}>{role}</MenuItem>
                ))}
              </Select>
            </div>

            {/* Performance Filter */}
            {/* <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Performance Level</label>
              <Select
                value={performanceFilter}
                onChange={(e) => setPerformanceFilter(e.target.value)}
                sx={{
                  backgroundColor: "white",
                  borderRadius: "10px",
                  height: "42px",
                  minWidth: "260px",
                  width: "260px",
                  ".MuiOutlinedInput-notchedOutline": {
                    borderWidth: "1px",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#6d28d9",
                  },
                }}
              >
                <MenuItem value="all">All Performance Levels</MenuItem>
                <MenuItem value="high">High Performers (≥4.0)</MenuItem>
                <MenuItem value="needsImprovement">Needs Improvement (&lt;3.3)</MenuItem>
                <MenuItem value="improving">Improving Trend</MenuItem>
                <MenuItem value="declining">Declining Trend</MenuItem>
              </Select>
            </div> */}

            {/* Minimum Sample Size */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Min Surveys</label>
              <TextField
                type="number"
                value={minSampleSize}
                onChange={(e) => setMinSampleSize(Number(e.target.value))}
                size="small"
                InputProps={{
                  inputProps: { min: 1 },
                  sx: {
                    backgroundColor: "white",
                    borderRadius: "10px",
                    width: "110px",
                    height: "42px",
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#6d28d9",
                    },
                  },
                }}
              />
            </div>
          </div>

          <div className="text-sm text-gray-500">
            Showing {sortedAndFilteredStats.length} of {teamStats.length} team members
          </div>
        </div>
      </div>

      {/* Quick Insights Banner - 3 Cards */}
      {/* <div className="grid grid-cols-3 gap-6"> */}
        {/* High Performers */}
        {/* <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
          <h4 className="text-sm font-semibold text-gray-900 mb-2">High Performers</h4>
          <p className="text-3xl font-bold text-gray-900">
            {categorizePerformers.high.length}
          </p>
          <p className="text-xs text-gray-600 mt-1">Rating ≥ 4.0</p>
        </div> */}

        {/* Average Performers */}
        {/* <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
          <h4 className="text-sm font-semibold text-gray-900 mb-2">Average Performers</h4>
          <p className="text-3xl font-bold text-gray-900">
            {categorizePerformers.average.length}
          </p>
          <p className="text-xs text-gray-600 mt-1">Rating 3.3 - 3.9</p>
        </div> */}

        {/* Needs Improvement */}
        {/* <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
          <h4 className="text-sm font-semibold text-gray-900 mb-2">Needs Improvement</h4>
          <p className="text-3xl font-bold text-gray-900">
            {categorizePerformers.needsImprovement.length}
          </p>
          <p className="text-xs text-gray-600 mt-1">Rating &lt; 3.3</p>
        </div> */}
      {/* </div> */}

      {/* Role Performance Overview - Horizontal Bar Chart */}
      {/* <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-gray-900">Performance by Role</h3>
        </div>

        {calculateRoleAverages.length > 0 ? (
          <>
            <ResponsiveContainer width="100%" height={Math.max(300, calculateRoleAverages.length * 60)}>
              <BarChart
                data={calculateRoleAverages}
                layout="vertical"
                margin={{ left: 100, right: 20, top: 5, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" domain={[0, 5]} />
                <YAxis type="category" dataKey="role" width={100} />
                <RechartsTooltip content={<CustomRoleTooltip />} />
                <Legend />
                <Bar dataKey="avgRating" name="Average Rating">
                  {calculateRoleAverages.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={
                        parseFloat(entry.avgRating) >= 4.5 ? '#22c55e' :
                          parseFloat(entry.avgRating) >= 4.0 ? '#84cc16' :
                            parseFloat(entry.avgRating) >= 3.5 ? '#eab308' :
                              parseFloat(entry.avgRating) >= 3.0 ? '#f97316' :
                                '#ef4444'
                      }
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer> */}

            {/* Legend */}
            {/* <div className="mt-4 flex items-center justify-center space-x-6 text-xs text-gray-500">
              <div className="flex items-center">
                <div className="w-3 h-3 rounded bg-green-500 mr-1"></div>
                <span>Excellent (≥4.5)</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 rounded bg-lime-500 mr-1"></div>
                <span>Good (4.0-4.5)</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 rounded bg-yellow-500 mr-1"></div>
                <span>Average (3.5-4.0)</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 rounded bg-orange-500 mr-1"></div>
                <span>Below Avg (3.0-3.5)</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 rounded bg-red-500 mr-1"></div>
                <span>Poor (&lt;3.0)</span>
              </div>
            </div>
          </>
        ) : (
          <div className="h-64 flex items-center justify-center text-gray-500">
            No role data available
          </div>
        )}
      </div> */}

      {/* Detailed Team Member Table */}
      <div className="bg-white rounded-lg shadow border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold text-gray-900">Individual Performance</h3>
              <p className="text-sm text-gray-600 mt-1">
                Click any team member for detailed analytics and recent surveys
              </p>
            </div>
          </div>
        </div>

        {sortedAndFilteredStats.length > 0 ? (
          <TableContainer>
            <Table>
              <TableHead className="bg-gray-50">
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'name'}
                      direction={orderBy === 'name' ? order : 'asc'}
                      onClick={() => handleSort('name')}
                    >
                      <span className="font-bold">Name</span>
                    </TableSortLabel>
                  </TableCell>

                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'role'}
                      direction={orderBy === 'role' ? order : 'asc'}
                      onClick={() => handleSort('role')}
                    >
                      <span className="font-bold">Role</span>
                    </TableSortLabel>
                  </TableCell>

                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'avgRating'}
                      direction={orderBy === 'avgRating' ? order : 'asc'}
                      onClick={() => handleSort('avgRating')}
                    >
                      <span className="font-bold">Avg Rating</span>
                    </TableSortLabel>
                  </TableCell>

                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'totalSurveys'}
                      direction={orderBy === 'totalSurveys' ? order : 'asc'}
                      onClick={() => handleSort('totalSurveys')}
                    >
                      <span className="font-bold">Surveys</span>
                    </TableSortLabel>
                  </TableCell>

                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'positivePercentage'}
                      direction={orderBy === 'positivePercentage' ? order : 'asc'}
                      onClick={() => handleSort('positivePercentage')}
                    >
                      <span className="font-bold">Positive %</span>
                    </TableSortLabel>
                  </TableCell>

                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'negativePercentage'}
                      direction={orderBy === 'negativePercentage' ? order : 'asc'}
                      onClick={() => handleSort('negativePercentage')}
                    >
                      <span className="font-bold">Negative %</span>
                    </TableSortLabel>
                  </TableCell>

                  <TableCell sx={{ width: '120px' }}>
                    <span className="font-bold">Distribution</span>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {sortedAndFilteredStats.map((member) => {
                  return (
                    <TableRow
                      key={member.id}
                      hover
                      onClick={() => handleRowClick(member)}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: '#f9fafb' }
                      }}
                    >
                      {/* Name */}
                      <TableCell>
                        <p className="font-semibold text-gray-900">{formatName(member.name)}</p>
                      </TableCell>

                      {/* Role */}
                      <TableCell>
                        <span className="px-3 py-1 bg-blue-100 text-blue-800 text-xs rounded-full font-medium">
                          {member.role}
                        </span>
                      </TableCell>

                      {/* Avg Rating */}
                      <TableCell>
                        <div className="flex items-center">
                          <StarIcon
                            className={`text-sm mr-1 ${parseFloat(member.avgRating) >= 4.5 ? 'text-green-600' :
                              parseFloat(member.avgRating) >= 4.0 ? 'text-lime-600' :
                                parseFloat(member.avgRating) >= 3.5 ? 'text-yellow-600' :
                                  parseFloat(member.avgRating) >= 3.0 ? 'text-orange-600' :
                                    'text-red-600'
                              }`}
                            sx={{ fontSize: '18px' }}
                          />
                          <span className={`font-bold ${parseFloat(member.avgRating) >= 4.5 ? 'text-green-600' :
                            parseFloat(member.avgRating) >= 4.0 ? 'text-lime-600' :
                              parseFloat(member.avgRating) >= 3.5 ? 'text-yellow-600' :
                                parseFloat(member.avgRating) >= 3.0 ? 'text-orange-600' :
                                  'text-red-600'
                            }`}>
                            {member.avgRating}
                          </span>
                        </div>
                      </TableCell>

                      {/* Total Surveys */}
                      <TableCell>
                        <span className="font-semibold text-gray-900">{member.totalSurveys}</span>
                      </TableCell>

                      {/* Positive % */}
                      <TableCell>
                        <div className="flex items-center">
                          <span className="text-green-700 font-semibold">{member.positivePercentage}%</span>
                          <span className="text-xs text-gray-500 ml-1">
                            ({member.ratingCounts[5]})
                          </span>
                        </div>
                      </TableCell>

                      {/* Negative % */}
                      <TableCell>
                        <div className="flex items-center">
                          <span className="text-red-700 font-semibold">{member.negativePercentage}%</span>
                          <span className="text-xs text-gray-500 ml-1">
                            ({member.ratingCounts[3] + member.ratingCounts[2] + member.ratingCounts[1]})
                          </span>
                        </div>
                      </TableCell>

                      {/* Rating Distribution Mini-Chart */}
                      <TableCell align="center">
                        <div className="flex items-center justify-center space-x-1">
                          {[5, 4, 3, 2, 1].map((rating) => {
                            const count = member.ratingCounts[rating];
                            const percentage = (count / member.totalSurveys) * 100;
                            return (
                              <Tooltip
                                key={rating}
                                title={`${rating}★: ${count} surveys (${percentage.toFixed(0)}%)`}
                              >
                                <div
                                  className="w-2 rounded-full transition-all cursor-help"
                                  style={{
                                    height: `${Math.max(percentage * 0.4, 4)}px`,
                                    maxHeight: '40px',
                                    backgroundColor:
                                      rating === 5 ? '#22c55e' :
                                        rating === 4 ? '#84cc16' :
                                          rating === 3 ? '#eab308' :
                                            rating === 2 ? '#f97316' :
                                              '#ef4444'
                                  }}
                                ></div>
                              </Tooltip>
                            );
                          })}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <div className="p-12 text-center">
            <p className="text-gray-500">No team members match the current filters</p>
          </div>
        )}
      </div>

      {/* Team Member Detail Modal */}
      <Modal open={openModal} onClose={handleCloseModal}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 1200,
            maxHeight: '96vh',
            overflowY: 'auto',
            bgcolor: 'background.paper',
            borderRadius: '16px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            border: 'none',
            p: 0
          }}
        >
          {selectedMember && (() => {
            const memberSurveys = getStaffSurveys(selectedMember.id);
            const trendData = calculateMemberTrend(selectedMember.id);
            const trendInfo = calculateTrendDirection(selectedMember.id);
            const recentReviews = memberSurveys;

            // Calculate role benchmark
            const roleMembers = teamStats.filter(m => m.role === selectedMember.role);
            const roleAvg = roleMembers.reduce((sum, m) => sum + parseFloat(m.avgRating), 0) / roleMembers.length;
            const vsRoleAvg = (parseFloat(selectedMember.avgRating) - roleAvg).toFixed(2);

            return (
              <>
                {/* Header Section */}
                <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 mx-0 mt-0 mb-0 z-10">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
                        <StarIcon className="text-violet-600" style={{ fontSize: "24px" }} />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-slate-900">
                          {formatName(selectedMember.name)}
                        </h3>
                        <div className="flex items-center gap-x-2">
                          <span className="text-sm text-slate-600">{selectedMember.role}</span>
                          <span className="text-slate-400">•</span>
                          <span className="text-sm text-slate-600">Last {dateRange} days</span>
                          <span className="text-slate-400">•</span>
                          <span className="text-sm text-slate-600">
                            {selectedMember.totalSurveys} {selectedMember.totalSurveys === 1 ? 'survey' : 'surveys'}
                          </span>
                          {trendInfo.trend === 'improving' && (
                            <>
                              <span className="text-slate-400">•</span>
                              <span className="inline-flex items-center text-sm text-green-700">
                                <TrendingUpIcon className="mr-1" sx={{ fontSize: '16px' }} />
                                Improving
                              </span>
                            </>
                          )}
                          {trendInfo.trend === 'declining' && (
                            <>
                              <span className="text-slate-400">•</span>
                              <span className="inline-flex items-center text-sm text-red-700">
                                <TrendingDownIcon className="mr-1" sx={{ fontSize: '16px' }} />
                                Trending Down
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="flex items-center justify-end">
                          <StarIcon className="text-yellow-400 mr-1" sx={{ fontSize: '32px' }} />
                          <span className="text-4xl font-bold text-slate-900">{selectedMember.avgRating}</span>
                        </div>
                        <p className="text-xs text-slate-500 mt-1">
                          {vsRoleAvg >= 0 ? '+' : ''}{vsRoleAvg} vs {selectedMember.role} avg
                        </p>
                      </div>
                      <button
                        className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-500 hover:text-gray-700 transition-colors"
                        onClick={handleCloseModal}
                      >
                        <CloseIcon />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Content Section */}
                <div className="p-6">
                  {/* Performance Stats Grid */}
                  <div className="grid grid-cols-5 gap-4 mb-6">
                    <div className="bg-white p-4 rounded-lg border border-slate-200">
                      <p className="text-sm text-slate-600 font-semibold">Excellent (5★)</p>
                      <p className="text-2xl font-bold text-green-600 mt-1">
                        {selectedMember.ratingCounts[5]}
                      </p>
                      <p className="text-xs text-slate-500">
                        {((selectedMember.ratingCounts[5] / selectedMember.totalSurveys) * 100).toFixed(1)}% of total
                      </p>
                    </div>

                    <div className="bg-white p-4 rounded-lg border border-slate-200">
                      <p className="text-sm text-slate-600 font-semibold">Good (4★)</p>
                      <p className="text-2xl font-bold text-lime-600 mt-1">
                        {selectedMember.ratingCounts[4]}
                      </p>
                      <p className="text-xs text-slate-500">
                        {((selectedMember.ratingCounts[4] / selectedMember.totalSurveys) * 100).toFixed(1)}% of total
                      </p>
                    </div>

                    <div className="bg-white p-4 rounded-lg border border-slate-200">
                      <p className="text-sm text-slate-600 font-semibold">Average (3★)</p>
                      <p className="text-2xl font-bold text-yellow-600 mt-1">
                        {selectedMember.ratingCounts[3]}
                      </p>
                      <p className="text-xs text-slate-500">
                        {((selectedMember.ratingCounts[3] / selectedMember.totalSurveys) * 100).toFixed(1)}% of total
                      </p>
                    </div>

                    <div className="bg-white p-4 rounded-lg border border-slate-200">
                      <p className="text-sm text-slate-600 font-semibold">Poor (2★)</p>
                      <p className="text-2xl font-bold text-orange-600 mt-1">
                        {selectedMember.ratingCounts[2]}
                      </p>
                      <p className="text-xs text-slate-500">
                        {((selectedMember.ratingCounts[2] / selectedMember.totalSurveys) * 100).toFixed(1)}% of total
                      </p>
                    </div>

                    <div className="bg-white p-4 rounded-lg border border-slate-200">
                      <p className="text-sm text-slate-600 font-semibold">Very Poor (1★)</p>
                      <p className="text-2xl font-bold text-red-600 mt-1">
                        {selectedMember.ratingCounts[1]}
                      </p>
                      <p className="text-xs text-slate-500">
                        {((selectedMember.ratingCounts[1] / selectedMember.totalSurveys) * 100).toFixed(1)}% of total
                      </p>
                    </div>
                  </div>

                  {/* Performance Trend Chart */}
                  <div className="mb-6">
                    <h4 className="text-lg font-semibold text-slate-900 mb-3">Performance Trend</h4>
                    {trendData.length >= 2 ? (
                      <ResponsiveContainer width="100%" height={280}>
                        <LineChart
                          data={trendData}
                          margin={{ top: 20, right: 20, left: 0, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis
                            dataKey="date"
                            tickFormatter={(date) => {
                              try {
                                return formatDateReadableMonthAndDay(date);
                              } catch (e) {
                                return date;
                              }
                            }}
                          />
                          <YAxis domain={[0, 5]} />
                          <RechartsTooltip
                            labelFormatter={(date) => {
                              try {
                                return formatDateReadableWithDayOfWeek(date);
                              } catch (e) {
                                return date;
                              }
                            }}
                            formatter={(value, name, props) => [
                              `${value} (${props.payload.count} ${props.payload.count === 1 ? 'survey' : 'surveys'})`,
                              'Avg Rating'
                            ]}
                          />
                          <Line
                            type="monotone"
                            dataKey="avgRating"
                            stroke="#6d28d9"
                            strokeWidth={3}
                            name="Daily Avg Rating"
                            dot={{ fill: '#6d28d9', r: 5 }}
                            activeDot={{ r: 8 }}
                          />

                          {/* Reference line at overall average */}
                          <ReferenceLine
                            y={parseFloat(selectedMember.avgRating)}
                            stroke="#6b7280"
                            strokeDasharray="3 3"
                            label=""
                          />

                          {/* Reference line at role average */}
                          <ReferenceLine
                            y={roleAvg}
                            stroke="#3b82f6"
                            strokeDasharray="5 5"
                            label=""
                          />

                          <Legend
                            wrapperStyle={{ paddingTop: '10px' }}
                            iconSize={18}
                            content={(props) => {
                              const { payload } = props;
                              return (
                                <div style={{ display: 'flex', justifyContent: 'center', gap: '40px', paddingTop: '10px' }}>
                                  {payload.map((entry, index) => {
                                    const isDashed = entry.id === 'overall' || entry.id === 'role';
                                    return (
                                      <div key={`legend-${index}`} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <svg width="18" height="2" style={{ overflow: 'visible' }}>
                                          <line
                                            x1="0"
                                            y1="1"
                                            x2="18"
                                            y2="1"
                                            stroke={entry.color}
                                            strokeWidth="2"
                                            strokeDasharray={isDashed ? (entry.id === 'overall' ? '3 3' : '5 5') : '0'}
                                          />
                                        </svg>
                                        <span style={{ color: '#475569', fontSize: '14px' }}>{entry.value}</span>
                                      </div>
                                    );
                                  })}
                                </div>
                              );
                            }}
                            payload={[
                              {
                                value: 'Daily Avg Rating',
                                type: 'line',
                                id: 'avgRating',
                                color: '#6d28d9'
                              },
                              {
                                value: 'Overall Avg',
                                type: 'line',
                                id: 'overall',
                                color: '#6b7280'
                              },
                              {
                                value: `${selectedMember.role} Avg`,
                                type: 'line',
                                id: 'role',
                                color: '#3b82f6'
                              }
                            ]}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg border border-gray-200">
                        <p className="text-gray-500">Insufficient data for trend analysis</p>
                      </div>
                    )}
                  </div>

                  {/* Comparison Section */}
                  <div className="mb-6">
                    <h4 className="text-lg font-semibold text-slate-900 mb-3">Performance Benchmarks</h4>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                        <p className="text-sm text-slate-600 mb-1">vs Overall Average</p>
                        <p className={`text-2xl font-bold ${
                          (parseFloat(selectedMember.avgRating) - parseFloat(overallStats.avgRating)).toFixed(2) >= 0
                            ? 'text-green-600'
                            : 'text-red-600'
                        }`}>
                          {(parseFloat(selectedMember.avgRating) - parseFloat(overallStats.avgRating)).toFixed(2) >= 0 ? '+' : ''}
                          {(parseFloat(selectedMember.avgRating) - parseFloat(overallStats.avgRating)).toFixed(2)}
                        </p>
                        <p className="text-xs text-slate-500 mt-1">
                          Team: {overallStats.avgRating}
                        </p>
                      </div>
                      <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                        <p className="text-sm text-slate-600 mb-1">vs {selectedMember.role} Average</p>
                        <p className={`text-2xl font-bold ${
                          vsRoleAvg >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {vsRoleAvg >= 0 ? '+' : ''}{vsRoleAvg}
                        </p>
                        <p className="text-xs text-slate-500 mt-1">
                          Role: {roleAvg.toFixed(2)}
                        </p>
                      </div>
                      <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                        <p className="text-sm text-slate-600 mb-1">Rank in {selectedMember.role}</p>
                        <p className="text-2xl font-bold text-violet-700">
                          {roleMembers.findIndex(m => m.id === selectedMember.id) + 1} of {roleMembers.length}
                        </p>
                        <p className="text-xs text-slate-500 mt-1">
                          {roleMembers.findIndex(m => m.id === selectedMember.id) + 1 === 1 ? 'Top performer' :
                           roleMembers.findIndex(m => m.id === selectedMember.id) + 1 === roleMembers.length ? 'Needs support' : 'Mid-tier'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Recent Reviews */}
                  <div>
                    <h4 className="text-lg font-semibold text-slate-900 mb-3">Recent Surveys</h4>
                    {recentReviews.length > 0 ? (
                      <div className="space-y-2">
                        {/* Column Headers */}
                        <div className="grid grid-cols-12 gap-3 px-4 py-2 bg-slate-50 rounded-lg border border-slate-200">
                          <div className="col-span-2 text-xs font-bold text-slate-700 uppercase tracking-wider">
                            Date/Time
                          </div>
                          <div className="col-span-2 text-xs font-bold text-slate-700 uppercase tracking-wider">
                            Rating
                          </div>
                          <div className="col-span-4 text-xs font-bold text-slate-700 uppercase tracking-wider">
                            Feedback
                          </div>
                          <div className="col-span-2 text-xs font-bold text-slate-700 uppercase tracking-wider">
                            Order Total
                          </div>
                          <div className="col-span-2 text-xs font-bold text-slate-700 uppercase tracking-wider">
                            Source
                          </div>
                        </div>

                        {recentReviews.map((review) => {
                          // Check if this member was the primary server
                          const isPrimary = review.staff_members.find(s => s.id === selectedMember.id)?.is_primary || false;
                          const ratingBgColor =
                            review.rating === 5 ? 'bg-green-500' :
                            review.rating === 4 ? 'bg-lime-500' :
                            review.rating === 3 ? 'bg-yellow-500' :
                            review.rating === 2 ? 'bg-orange-500' : 'bg-red-500';

                          const sourceBadge =
                            review.source === 'toast api' ? { bg: 'bg-blue-100', text: 'text-blue-800' } :
                            review.source === 'dine in' ? { bg: 'bg-purple-100', text: 'text-purple-800' } :
                            review.source === 'NFC' ? { bg: 'bg-green-100', text: 'text-green-800' } :
                            { bg: 'bg-gray-100', text: 'text-gray-800' };

                          // Format date from the precomputed 'date' field (already in store timezone)
                          const formatStoreDateShort = (dateStr) => {
                            const [year, month, day] = dateStr.split('-');
                            const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                            return `${monthNames[parseInt(month) - 1]} ${parseInt(day)}`;
                          };

                          // Format time from decimal_hour (already in store timezone)
                          const formatStoreTime = (decimalHour) => {
                            const hour = Math.floor(decimalHour);
                            const minute = Math.floor((decimalHour - hour) * 60);
                            const period = hour >= 12 ? 'PM' : 'AM';
                            const displayHour = hour % 12 || 12;
                            return `${displayHour}:${minute.toString().padStart(2, '0')} ${period}`;
                          };

                          return (
                            <div
                              key={review.id}
                              className="bg-white px-4 py-3 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors"
                            >
                              <div className="grid grid-cols-12 gap-3 items-center">
                                {/* Date & Time */}
                                <div className="col-span-2">
                                  <div className="text-sm font-medium text-slate-900">
                                    {formatStoreDateShort(review.date)}
                                  </div>
                                  <div className="text-xs text-slate-600">
                                    {formatStoreTime(review.decimal_hour)}
                                  </div>
                                </div>

                                {/* Rating */}
                                <div className="col-span-2 flex items-center gap-2">
                                  <span className={`${ratingBgColor} text-white px-2.5 py-1 rounded-md font-bold text-sm`}>
                                    {review.rating}
                                  </span>
                                  <div className="flex">
                                    {[...Array(5)].map((_, i) => (
                                      <StarIcon
                                        key={i}
                                        className={`${i < review.rating ? 'text-yellow-400' : 'text-gray-300'}`}
                                        sx={{ fontSize: '16px' }}
                                      />
                                    ))}
                                  </div>
                                </div>

                                {/* Feedback */}
                                <div className="col-span-4">
                                  {review.feedback ? (
                                    <p className="text-sm text-slate-600 italic">
                                      "{review.feedback.length > 80 ? `${review.feedback.substring(0, 80)}...` : review.feedback}"
                                    </p>
                                  ) : (
                                    <p className="text-sm text-slate-400 italic">No feedback provided</p>
                                  )}
                                </div>

                                {/* Order Total */}
                                <div className="col-span-2">
                                  {review.source === 'toast api' && review.order_total ? (
                                    <span className="text-sm font-medium text-slate-900">
                                      ${parseFloat(review.order_total).toFixed(2)}
                                    </span>
                                  ) : (
                                    <span className="text-sm text-slate-400">-</span>
                                  )}
                                </div>

                                {/* Source & Badges */}
                                <div className="col-span-2 flex flex-col gap-1 items-start">
                                  <span className={`${sourceBadge.bg} ${sourceBadge.text} px-2 py-1 rounded text-xs font-medium text-center`}>
                                    {review.source}
                                  </span>
                                  {isPrimary && (
                                    <span className="px-2 py-0.5 bg-violet-100 text-violet-700 text-xs rounded-md font-medium text-center">
                                      Primary
                                    </span>
                                  )}
                                  {review.staff_members.length > 1 && (
                                    <span className="text-xs text-slate-500 text-center">
                                      +{review.staff_members.length - 1} staff
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="p-8 text-center bg-gray-50 rounded-lg border border-gray-200">
                        <p className="text-gray-500">No surveys available</p>
                      </div>
                    )}
                  </div>

                </div>
              </>
            );
          })()}
        </Box>
      </Modal>
    </div>
  );
}
